import { useEffect, useRef, useState } from 'react'
import { db } from '@core/db/db'
import { syncService } from '@core/services/sync/SyncService'
import { META_DEVICE_TOKEN, META_COMPANY_NAME, META_COMPANY_INN, META_TENANT_ID } from '@core/db/schema'
import {
  clearCloudAdminToken,
  CLOUD_ADMIN_SESSION_EVENT,
  getCloudAdminToken,
  getCloudRefreshToken,
  setCloudAdminToken,
  setCloudRefreshToken
} from '@core/auth/cloudSession'
import { CLOUD_API } from './shared'
import { credInput, errBanner, okBanner } from '@ui/admin'
import { btn, btnBase, btnSize, cx } from '@ui/classes'

/**
 * Подключение к облаку: вход/выход и статус.
 *
 * Токен/tenant_id хранятся в Dexie meta; после входа запускается syncService.
 * Публикация каталога и журнал чеков живут в своих разделах (Каталог/Продажи).
 */
export function CloudAccountSection() {
  const [token, setToken] = useState<string | null>(null)
  const [companyName, setCompanyName] = useState<string>('')
  const [login, setLogin] = useState('admin')
  const [password, setPassword] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const noteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /** Показать сообщение; финальные статусы скрываются сами. */
  const showNote = (text: string, autoHideMs?: number) => {
    if (noteTimerRef.current) clearTimeout(noteTimerRef.current)
    setNote(text)
    if (autoHideMs) {
      noteTimerRef.current = setTimeout(() => setNote(null), autoHideMs)
    }
  }

  useEffect(
    () => () => {
      if (noteTimerRef.current) clearTimeout(noteTimerRef.current)
    },
    []
  )

  // Admin-сессия — только текущая вкладка. Device-token в Dexie не даёт
  // доступа к админским разделам.
  useEffect(() => {
    const sync = () => setToken(getCloudAdminToken() || null)
    sync()
    window.addEventListener(CLOUD_ADMIN_SESSION_EVENT, sync)
    void (async () => {
      const cn = await db.meta.get(META_COMPANY_NAME)
      if (cn && typeof cn.value === 'string') setCompanyName(cn.value)
    })()
    return () => window.removeEventListener(CLOUD_ADMIN_SESSION_EVENT, sync)
  }, [])

  // При наличии токена подтягиваем название организации из облака и кэшируем.
  useEffect(() => {
    if (!token) return
    void (async () => {
      try {
        const resp = await fetch(`${CLOUD_API}/tenant/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!resp.ok) return
        const p = (await resp.json()) as { company_name?: string; inn?: string }
        if (p.company_name) {
          setCompanyName(p.company_name)
          await db.meta.put({ key: META_COMPANY_NAME, value: p.company_name })
        }
        if (p.inn) await db.meta.put({ key: META_COMPANY_INN, value: p.inn })
      } catch {
        /* офлайн — используем кэш из meta */
      }
    })()
  }, [token])

  const doLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    setNote(null)
    setBusy(true)
    try {
      const resp = await fetch(`${CLOUD_API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login, password })
      })
      if (!resp.ok) {
        setErr('Неверный логин или пароль')
        return
      }
      const data = (await resp.json()) as {
        token: string
        refresh_token?: string
        user: { tenant_id: string }
      }
      // Касса получает отдельный device-token. Admin-JWT остаётся только
      // в памяти/сессии текущей вкладки и не попадает в Dexie.
      const deviceResp = await fetch(`${CLOUD_API}/auth/device`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${data.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      })
      if (!deviceResp.ok) {
        throw new Error('Не удалось получить токен устройства')
      }
      const device = (await deviceResp.json()) as { token: string }
      await db.meta.put({ key: META_DEVICE_TOKEN, value: device.token })
      await db.meta.put({ key: META_TENANT_ID, value: data.user.tenant_id })
      setCloudAdminToken(data.token)
      setCloudRefreshToken(data.refresh_token ?? '')
      setToken(data.token)
      setPassword('')
      showNote('Вход в облако выполнен, синхронизация…')
      // Сразу тянем данные в кассу device-token-ом. Финальный статус
      // показываем после завершения и скрываем сами.
      void syncService
        .syncAll()
        .then(() => showNote('Синхронизация завершена', 5000))
        .catch(() =>
          showNote('Вход выполнен, но синхронизация не удалась — повторим автоматически', 7000)
        )
    } catch (error) {
      setErr(error instanceof Error ? error.message : 'Нет связи с облаком')
    } finally {
      setBusy(false)
    }
  }

  const logout = async () => {
    const refreshToken = getCloudRefreshToken()
    if (refreshToken) {
      try {
        await fetch(`${CLOUD_API}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken })
        })
      } catch {
        /* локальный logout всё равно выполняем */
      }
    }
    await db.meta.delete(META_DEVICE_TOKEN)
    await db.meta.delete(META_TENANT_ID)
    await db.meta.delete(META_COMPANY_NAME)
    clearCloudAdminToken()
    setCloudRefreshToken('')
    setToken(null)
    setCompanyName('')
    showNote('Выход из облака выполнен', 5000)
  }

  return (
    <div>
      {note && <p className={okBanner}>{note}</p>}
      {err && <p className={errBanner}>{err}</p>}

      {!token ? (
        <form
          onSubmit={doLogin}
          className="flex flex-wrap items-end gap-2"
        >
          <label className="flex flex-col gap-1 text-small">
            Логин
            <input
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              className={credInput}
            />
          </label>
          <label className="flex flex-col gap-1 text-small">
            Пароль
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={credInput}
            />
          </label>
          <button type="submit" disabled={busy} className={cx(btnBase, btn.secondary, btnSize.sm)}>
            {busy ? 'Вход…' : 'Войти в облако'}
          </button>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-semibold text-okDark">
            ● Подключено к облаку: {companyName || 'Организация'}
          </span>
          <button onClick={() => void logout()} disabled={busy} className={cx(btnBase, btn.secondary, btnSize.sm)}>
            Выйти из облака
          </button>
        </div>
      )}
    </div>
  )
}
